<template>
  <div class="monthly-market-update-wrapper">
    <!-- Archive View -->
    <div v-if="currentView === 'archive'" class="archive-view">
      <MarketUpdatesArchive @navigate-to-update="showUpdateDetail" />
    </div>

    <!-- Detail View -->
    <div v-else class="detail-view">
      <div class="monthly-market-update">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading market update...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <i class="pi pi-exclamation-circle"></i>
      <h2>Update Not Found</h2>
      <p>{{ error }}</p>
      <button class="back-btn" @click="backToArchive">Back to Archive</button>
    </div>

    <!-- Content -->
    <div v-else-if="updateData" class="update-content">
      <!-- Header -->
      <div class="update-header">
        <button class="archive-btn" @click="backToArchive">
          <i class="pi pi-th"></i> View All Updates
        </button>
        <h1 class="update-title">{{ updateData.title }}</h1>
        <div class="update-meta">
          <span class="meta-item">As of {{ formatDate(updateData.asOfDate) }}</span>
          <span class="meta-divider">|</span>
          <span class="meta-item">Last updated: {{ formatDate(updateData.lastUpdatedAt) }}</span>
          <span class="meta-divider">|</span>
          <span class="meta-item">Version {{ updateData.version }}</span>
        </div>
        <div class="action-buttons">
          <button class="action-btn" @click="printUpdate" title="Print or Save as PDF">
            <i class="pi pi-print"></i> Print/Save
          </button>
          <button class="action-btn" @click="shareUpdate" title="Copy link to clipboard">
            <i class="pi pi-share-alt"></i> Share Link
          </button>
        </div>
      </div>

      <!-- 1-Minute Summary -->
      <div class="summary-section">
        <div class="section-icon">📊</div>
        <h2 class="section-title">1-Minute Summary</h2>
        <div class="summary-card">
          <p class="summary-text">{{ updateData.oneMinuteSummary }}</p>
        </div>
      </div>

      <!-- 3 Key Market Themes -->
      <div class="themes-section">
        <div class="section-icon">🔍</div>
        <h2 class="section-title">3 Key Market Themes</h2>
        <div class="themes-list">
          <AccordionItem
            v-for="(theme, index) in updateData.themes"
            :key="theme.id"
            :id="theme.id"
            :title="`${index + 1}. ${theme.title}`"
            :default-expanded="index === 0"
          >
            <p class="theme-body">{{ theme.body }}</p>
          </AccordionItem>
        </div>
      </div>

      <!-- Portfolio Implications -->
      <div class="implications-section">
        <div class="section-icon">💼</div>
        <h2 class="section-title">What This Means for Portfolios</h2>
        <div class="implications-card">
          <ul class="implications-list">
            <li v-for="(implication, index) in updateData.portfolioImplications" :key="index">
              {{ implication }}
            </li>
          </ul>
        </div>
      </div>

      <!-- FAQs -->
      <div class="faqs-section">
        <div class="section-icon">❓</div>
        <h2 class="section-title">Frequently Asked Questions</h2>
        <div class="faqs-list">
          <AccordionItem
            v-for="faq in updateData.faqs"
            :key="faq.id"
            :id="faq.id"
            :title="faq.question"
          >
            <p class="faq-answer">{{ faq.answer }}</p>
          </AccordionItem>
        </div>
      </div>

      <!-- Disclaimer -->
      <div class="disclaimer-section">
        <h3 class="disclaimer-title">Important Regulatory Disclaimer</h3>
        <p class="disclaimer-text">{{ updateData.disclaimer }}</p>
      </div>

      <!-- Tags -->
      <div v-if="updateData.tags && updateData.tags.length > 0" class="tags-section">
        <span class="tags-label">Topics:</span>
        <span
          v-for="tag in updateData.tags"
          :key="tag"
          class="tag-badge"
        >
          {{ formatTag(tag) }}
        </span>
      </div>
    </div>

    <!-- Share Success Toast -->
    <div v-if="showShareToast" class="share-toast">
      Link copied to clipboard!
    </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import AccordionItem from '@/components/market-updates/AccordionItem.vue';
import MarketUpdatesArchive from '@/sections/market-updates/MarketUpdatesArchive.vue';

// State
const currentView = ref('archive'); // 'archive' or 'detail'
const selectedUpdateId = ref(null);
const updateData = ref(null);
const isLoading = ref(false);
const error = ref(null);
const showShareToast = ref(false);

// Methods
const showUpdateDetail = (id) => {
  selectedUpdateId.value = id;
  currentView.value = 'detail';
  loadUpdate(id);
  // Scroll to top when navigating to detail
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const backToArchive = () => {
  currentView.value = 'archive';
  updateData.value = null;
  error.value = null;
  selectedUpdateId.value = null;
  // Scroll to top when going back to archive
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const loadUpdate = async (id) => {
  isLoading.value = true;
  error.value = null;

  try {
    // Use dynamic import for production compatibility
    const updateModule = await import(`@/assets/contents/market-updates/${id}.json`);
    const data = updateModule.default || updateModule;
    updateData.value = data;
  } catch (err) {
    console.error('Error loading update:', err);
    error.value = 'The requested market update could not be found. It may have been removed or the link may be incorrect.';
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatTag = (tag) => {
  return tag.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

const printUpdate = () => {
  window.print();
};

const shareUpdate = async () => {
  const url = window.location.href;
  try {
    await navigator.clipboard.writeText(url);
    showShareToast.value = true;
    setTimeout(() => {
      showShareToast.value = false;
    }, 3000);
  } catch (err) {
    console.error('Failed to copy link:', err);
    // Fallback: show alert
    alert(`Copy this link: ${url}`);
  }
};

// Lifecycle
onMounted(async () => {
  // Load the latest update by default
  try {
    // Use dynamic import instead of fetch
    const indexModule = await import('@/assets/contents/market-updates/index.json');
    const data = indexModule.default || indexModule;
    const publishedUpdates = data.updates.filter(u => u.status === 'published');

    if (publishedUpdates.length > 0) {
      // Sort by date to get the latest
      const sortedUpdates = publishedUpdates.sort((a, b) =>
        new Date(b.asOfDate) - new Date(a.asOfDate)
      );
      const latestUpdate = sortedUpdates[0];

      // Show the latest update in detail view
      showUpdateDetail(latestUpdate.id);
    } else {
      // No published updates, show archive
      currentView.value = 'archive';
    }
  } catch (err) {
    console.error('Error loading latest update:', err);
    // Fallback to archive view
    currentView.value = 'archive';
  }
});
</script>

<style scoped>
.monthly-market-update-wrapper {
  width: 100%;
  min-height: 100vh;
  background: #f8f9fa;
}

.archive-view,
.detail-view {
  width: 100%;
}

.monthly-market-update {
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
}

/* Content wrapper for better readability on very large screens */
.update-content {
  max-width: 1600px;
  margin: 0 auto;
}

/* Loading & Error States */
.loading-container,
.error-container {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid var(--an-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container i {
  font-size: 4rem;
  color: #dc3545;
  margin-bottom: 16px;
  display: block;
}

.error-container h2 {
  margin: 0 0 12px 0;
  color: var(--an-text-primary);
}

.back-btn {
  margin-top: 20px;
  padding: 12px 24px;
  background: var(--an-primary);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
}

/* Header */
.update-header {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.archive-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--an-primary);
  font-weight: 600;
  cursor: pointer;
  background: white;
  border: 1px solid var(--an-primary);
  font-size: 0.9rem;
  padding: 8px 14px;
  border-radius: 6px;
  transition: all 0.2s;
  margin-bottom: 16px;
}

.archive-btn:hover {
  background: var(--an-primary);
  color: white;
}

.archive-btn i {
  font-size: 0.85rem;
}

.update-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
  line-height: 1.2;
}

.update-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--an-text-muted);
  margin-bottom: 16px;
}

.meta-item {
  font-weight: 500;
}

.meta-divider {
  color: #d0d0d0;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  padding: 8px 16px;
  background: #f0f4f8;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.action-btn:hover {
  background: var(--an-primary);
  color: white;
  border-color: var(--an-primary);
}

.action-btn i {
  font-size: 0.85rem;
}

/* Sections */
.summary-section,
.themes-section,
.implications-section,
.faqs-section {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-icon {
  font-size: 1.5rem;
  margin-bottom: 8px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
}

/* Summary */
.summary-card {
  background: linear-gradient(135deg, #f0f4f8 0%, #e8f0f8 100%);
  border-left: 4px solid var(--an-primary);
  border-radius: 8px;
  padding: 16px;
}

.summary-text {
  font-size: 1.05rem;
  line-height: 1.7;
  color: var(--an-text-primary);
  margin: 0;
}

/* Themes */
.themes-list {
  margin-top: 16px;
}

.theme-body {
  margin: 0;
  line-height: 1.7;
}

/* Implications */
.implications-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.implications-list {
  margin: 0;
  padding-left: 24px;
  line-height: 1.8;
}

.implications-list li {
  margin-bottom: 12px;
  color: var(--an-text-primary);
}

.implications-list li:last-child {
  margin-bottom: 0;
}

/* FAQs */
.faqs-list {
  margin-top: 16px;
}

.faq-answer {
  margin: 0;
  line-height: 1.7;
}

/* Disclaimer */
.disclaimer-section {
  background: #fff9f0;
  border: 2px solid #ffd699;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;
}

.disclaimer-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #856404;
  margin: 0 0 12px 0;
}

.disclaimer-text {
  font-size: 0.85rem;
  line-height: 1.6;
  color: #856404;
  margin: 0;
}

/* Tags */
.tags-section {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tags-label {
  font-weight: 600;
  color: var(--an-text-muted);
  font-size: 0.9rem;
}

.tag-badge {
  padding: 4px 10px;
  background: #f0f4f8;
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--an-primary);
  font-weight: 500;
}

/* Share Toast */
.share-toast {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background: #28a745;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  font-weight: 600;
  animation: slideIn 0.3s ease-out;
  z-index: 1000;
}

@keyframes slideIn {
  from {
    transform: translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Print Styles */
@media print {
  .action-buttons,
  .archive-btn,
  .share-toast {
    display: none !important;
  }

  .monthly-market-update {
    background: white;
  }

  .update-header,
  .summary-section,
  .themes-section,
  .implications-section,
  .faqs-section,
  .disclaimer-section,
  .tags-section {
    box-shadow: none;
    page-break-inside: avoid;
  }
}

/* Responsive */
@media (max-width: 1200px) {
  .monthly-market-update {
    padding: 16px;
  }
}

@media (max-width: 1024px) {
  .monthly-market-update {
    padding: 16px;
  }
}

@media (max-width: 768px) {
  .monthly-market-update {
    padding: 12px;
  }

  .update-header,
  .summary-section,
  .themes-section,
  .implications-section,
  .faqs-section,
  .disclaimer-section {
    padding: 16px;
  }

  .update-title {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .update-meta {
    flex-direction: column;
    gap: 4px;
  }

  .meta-divider {
    display: none;
  }

  .summary-text {
    font-size: 1rem;
  }

  .summary-card,
  .implications-card {
    padding: 14px;
  }

  .share-toast {
    bottom: 20px;
    right: 20px;
    left: 20px;
    text-align: center;
  }
}

@media (max-width: 640px) {
  .monthly-market-update {
    padding: 10px;
  }

  .update-header,
  .summary-section,
  .themes-section,
  .implications-section,
  .faqs-section,
  .disclaimer-section {
    padding: 14px;
  }

  .summary-card,
  .implications-card {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .monthly-market-update {
    padding: 8px;
  }

  .update-header,
  .summary-section,
  .themes-section,
  .implications-section,
  .faqs-section,
  .disclaimer-section {
    padding: 12px;
  }

  .update-title {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .summary-text {
    font-size: 0.95rem;
  }

  .summary-card,
  .implications-card {
    padding: 10px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .implications-list {
    padding-left: 18px;
    font-size: 0.9rem;
  }

  .disclaimer-text {
    font-size: 0.8rem;
  }

  .tags-section {
    padding: 10px;
  }
}
</style>
